/* The kinds that are only text and a surface: prose, a heading, a rule, a cut
   line, a list, a quotation, a byline, a boxed statement, a figure.

   None of them holds state or behaviour, which is why they share a file: eight
   files of four lines each would say less about the material than this one page
   of it does. The kinds that do something of their own — the exercise, the
   cards, the question, the file maker — each have their own.

   OWNED BY ITEM 3 OF docs/plan/00-prestavba-2026-08.md. */

import { Head, Notes, T } from "./shared";
import { FIGURES } from "../lib/figures";
import { useI18n } from "../lib/i18n";
import type {
  CalloutBlock, CutBlock, FigureBlock, HeadingBlock, PointsBlock, QuotesBlock,
  SigBlock, TextBlock,
} from "../lib/types";

export function Text({ b }: { b: TextBlock }) {
  return <div className="prose"><T value={b.body} block /></div>;
}

export function Rule() {
  return <hr className="rule-heavy" />;
}

/* Where the sheet is cut.

   The material that gets cut up — cards for a pairing game, strips a pupil
   takes away, a slip that comes back filled in — is the one a teacher most
   often prints wrong, because nothing on the page said it was ever meant to be
   cut. So the cut is drawn: a dashed line and a pair of scissors, at the point
   the material itself says the paper is divided.

   It is the same mark on the screen and on paper. On paper it is the guide
   somebody actually cuts along, which is why it survives print.css rather than
   being chrome; on the screen it is what tells the teacher what they are
   looking at before they print anything.

   The scissors are drawn here rather than in figures.ts for the same reason the
   device shell is drawn in CSS: figures.ts is the registry of drawings a
   content author names by key, and this is a piece of one block's markup that
   nobody chooses. */
export function Cut({ b }: { b: CutBlock }) {
  const { ui } = useI18n();
  return (
    <div className="cut" role="separator">
      <span className="cut-mark" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="17" height="17">
          <g fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
            <circle cx="6" cy="18" r="2.6" />
            <circle cx="18" cy="18" r="2.6" />
            <path d="M7.8 16.2 19 3M16.2 16.2 5 3" />
          </g>
        </svg>
      </span>
      <span className="cut-said">{b.title ? <T value={b.title} /> : ui("block.cut")}</span>
    </div>
  );
}

/* A section heading, not another block title: it is the boundary the reader
   scrolls by, so it is set a whole step above an h3 and carries a rule above it
   on a scrolling page — see .cat-content .block[data-k="h"] in base.css. */
export function Heading({ b }: { b: HeadingBlock }) {
  return (
    <>
      {b.title ? <h2><T value={b.title} /></h2> : null}
      {b.lead ? <div className="lead"><T value={b.lead} block /></div> : null}
    </>
  );
}

export function Points({ b }: { b: PointsBlock }) {
  return (
    <>
      <Head b={b} />
      <ul className="points">{b.points.map((p, i) => <li key={i}><T value={p} /></li>)}</ul>
      <Notes b={b} />
    </>
  );
}

export function Callout({ b }: { b: CalloutBlock }) {
  return (
    <div className={"callout" + (b.red ? " red" : "")}>
      <Head b={b} />
      {b.body ? <T value={b.body} block /> : null}
      {b.points?.length
        ? <ul className="points">{b.points.map((p, i) => <li key={i}><T value={p} /></li>)}</ul>
        : null}
    </div>
  );
}

export function Quotes({ b }: { b: QuotesBlock }) {
  return (
    <>
      <Head b={b} />
      {b.items.map((q, i) => <blockquote key={i} className="quote"><T value={q} /></blockquote>)}
    </>
  );
}

/* A byline: small, muted and set low. The opening slide is the only place for
   it — anywhere else it reads as a caption that lost its figure. */
export function Sig({ b }: { b: SigBlock }) {
  return <div className="sig">{b.lines.map((l, i) => <div key={i}><T value={l} /></div>)}</div>;
}

/* Generated SVG, no image files anywhere — see lib/figures.ts. A missing figure
   is a content bug and says so rather than leaving a silent hole. */
export function Figure({ b }: { b: FigureBlock }) {
  const draw = FIGURES[b.fig];
  return (
    <figure className="figure">
      {draw
        ? <div dangerouslySetInnerHTML={{ __html: draw(b.opts) }} />
        : <div className="block-unknown">?{b.fig}</div>}
      {b.caption ? <figcaption><T value={b.caption} /></figcaption> : null}
    </figure>
  );
}
